import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RocketOutlined } from '@ant-design/icons';
import { CardActionMenu } from '../shared';

interface ProjectCardProps {
  id?: string;
  name: string;
  status: 'running' | 'stopped' | 'deploying' | 'error';
  lastDeploy: string;
  type: string;
  url?: string;
  siteUrl?: string;
  technologyImage?: string;
  detailRoute?: string;
  /** Habilita Iniciar/Detener/Reiniciar en el menú (marketplace/single-instance). Deploy Apps => false */
  supportsLifecycle?: boolean;
  /** Refresca la lista tras una acción del menú */
  onRefresh?: () => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  id,
  name,
  status,
  lastDeploy,
  type,
  url,
  siteUrl,
  technologyImage,
  detailRoute,
  supportsLifecycle = false,
  onRefresh
}) => {
  const navigate = useNavigate();
  const resolvedDetailRoute = detailRoute || (id ? `/deploy-apps/${id}` : '');
  const statusConfig = {
    running: { color: 'bg-green-500', text: 'Running', textColor: 'text-green-700' },
    stopped: { color: 'bg-gray-500', text: 'Stopped', textColor: 'text-gray-700' },
    deploying: { color: 'bg-blue-500', text: 'Deploying', textColor: 'text-blue-700' },
    error: { color: 'bg-red-500', text: 'Error', textColor: 'text-red-700' },
  };

  const currentStatus = statusConfig[status];

  const handleClick = () => {
    if (detailRoute) {
      navigate(detailRoute);
    } else if (id) {
      navigate(`/deploy-apps/${id}`);
    }
  };

  return (
    <div 
      onClick={handleClick}
      className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-all hover:border-gray-300 dark:hover:border-gray-600 group ${(detailRoute || id) ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          {technologyImage ? (
            <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center bg-white dark:bg-gray-400 border border-gray-200 dark:border-gray-600">
              <img 
                src={technologyImage} 
                alt={type}
                className="w-full h-full object-contain p-1"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).parentElement!.innerHTML = '<div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white"><svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path d="M13 7H7v6h6V7z"/><path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd"/></svg></div>';
                }}
              />
            </div>
          ) : (
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white">
              <RocketOutlined className="text-lg" />
            </div>
          )}
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
              {name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{type}</p>
          </div>
        </div>
        
        {(detailRoute || id) && (
          <CardActionMenu
            serviceId={id || ''}
            status={status}
            detailRoute={resolvedDetailRoute}
            serviceName={name}
            onRefresh={onRefresh}
            supportsLifecycle={supportsLifecycle && !!id}
            canDelete={!!id}
          />
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 ${currentStatus.color} rounded-full ${status === 'deploying' ? 'animate-pulse' : ''}`}></div>
          <span className={`text-xs font-medium ${currentStatus.textColor} dark:text-gray-300`}>
            {currentStatus.text}
          </span>
        </div>
        
        <span className="text-xs text-gray-500 dark:text-gray-400">{lastDeploy}</span>
      </div>

      {siteUrl && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <a 
            href={siteUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            {siteUrl} →
          </a>
        </div>
      )}
    </div>
  );
};

export default ProjectCard;